import { Children, isValidElement, type ReactElement, type ReactNode } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

interface FenceProps {
  'data-title'?: string;
  'data-language'?: string;
}

/**
 * Tabs over the code fences inside it. A tab's label is `labels[i]`, else the fence's
 * `title="..."` meta, else its language: `<CodeTabs>` then ```bash title="curl"```.
 */
export function CodeTabs({ labels, children }: { labels?: string[]; children: ReactNode }) {
  const fences = Children.toArray(children).filter((child): child is ReactElement<FenceProps> =>
    isValidElement(child),
  );
  if (fences.length === 0) return null;
  const labelOf = (fence: ReactElement<FenceProps>, index: number) =>
    labels?.[index] ??
    fence.props['data-title'] ??
    fence.props['data-language'] ??
    String(index + 1);

  return (
    <Tabs defaultValue="0" className="my-5 gap-0">
      <TabsList variant="line" className="border-b px-1">
        {fences.map((fence, index) => (
          <TabsTrigger key={index} value={String(index)}>
            {labelOf(fence, index)}
          </TabsTrigger>
        ))}
      </TabsList>
      {fences.map((fence, index) => (
        <TabsContent key={index} value={String(index)} className="[&>div]:mt-2 [&>div]:mb-0">
          {fence}
        </TabsContent>
      ))}
    </Tabs>
  );
}
